import { CloseOutlined, UsergroupAddOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { TeamIcon, UserIcon } from 'assets/images/svg';
import { useManagePermittedRoles } from 'library/hooks';
import { formatDisplayName } from 'library/helpers/utility';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectTeamsByTeamID } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Button, Col, Divider, Empty, Flex, Modal, Row, Select, Tag, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;

const ManageMembersModal = ({ children, visible: visibleProp, setVisible: setVisibleProp, teamID }) => {
	const {
		paddingLG,
		paddingXS,
		paddingSM,
		fontSizeSM,
		colorTextDescription,
		paddingXL,
		fontWeightStrong,
		colorIcon,
		paddingXXS
	} = theme.useToken().token;
	const dispatch = useDispatch();

	const people = useSelector(selectPeople);
	const team = useSelector((state) => selectTeamsByTeamID(state, teamID));
	const { loadingAddMembersTeam } = useSelector((state) => state.Teams);

	const [newMembers, setNewMembers] = useState([]);
	const [removedMembers, setRemovedMembers] = useState([]);
	const [hasChanges, setHasChanges] = useState(false);
	const [internalVisible, setInternalVisible] = useState(false);

	const canManageMembers = useManagePermittedRoles('manageMembers');
	const permissionDeniedMessage = "You don't have permission to perform this action.";

	const visible = visibleProp ?? internalVisible;
	const setVisible = setVisibleProp ?? setInternalVisible;

	const availablePeople = useMemo(() => {
		const existing = team?.members || [];
		const filtered = existing.filter((id) => !removedMembers.includes(id));
		const excluded = new Set([...filtered, ...newMembers]);
		return people
			.filter((p) => !excluded.has(p.personID))
			.map((p) => ({ label: p.displayName, value: p.personID }));
	}, [people, team?.members, newMembers, removedMembers]);

	const handleCancel = () => {
		setNewMembers([]);
		setRemovedMembers([]);
		setHasChanges(false);
		setVisible(false);
	};

	const handleAddPerson = (personID) => {
		if (removedMembers.includes(personID)) {
			const updatedRemovedMembers = removedMembers.filter((id) => id !== personID);
			setRemovedMembers(updatedRemovedMembers);
			setHasChanges(newMembers.length > 0 || updatedRemovedMembers.length > 0);
		} else {
			const updatedNewMembers = [...newMembers, personID];
			setNewMembers(updatedNewMembers);
			setHasChanges(updatedNewMembers.length > 0 || removedMembers.length > 0);
		}
	};

	const handleRemovePerson = (personID) => {
		if (newMembers.includes(personID)) {
			const updatedNewMembers = newMembers.filter((id) => id !== personID);
			setNewMembers(updatedNewMembers);
			setHasChanges(updatedNewMembers.length > 0 || removedMembers.length > 0);
		} else {
			const updatedRemovedMembers = [...removedMembers, personID];
			setRemovedMembers(updatedRemovedMembers);
			setHasChanges(newMembers.length > 0 || updatedRemovedMembers.length > 0);
		}
	};

	const handleSubmit = () => {
		const membersToAdd = newMembers.filter((id) => !removedMembers.includes(id));
		const membersToRemove = removedMembers.filter((id) => !newMembers.includes(id));

		if (membersToAdd.length > 0) {
			dispatch(teamsActions.addTeamMember({ teamID, personIDs: membersToAdd }));
		}

		if (membersToRemove.length > 0) {
			dispatch(teamsActions.deleteTeamMember({ teamID, personIDs: membersToRemove }));
		}

		handleCancel();
	};

	return (
		<>
			<Tooltip title={!canManageMembers ? permissionDeniedMessage : 'Manage Members'} placement='top'>
				<span onClick={(e) => canManageMembers && setVisible(true)}>
					{children ? (
						children
					) : (
						<Button
							type='text'
							size='small'
							icon={<TeamIcon color='currentColor' />}
							disabled={!canManageMembers}
						/>
					)}
				</span>
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closable
				centered
				destroyOnClose
				width={763}
				footer={[
					<>
						<Divider />

						<Flex align='center' justify='flex-end' gap={paddingSM} key='footer'>
							<Button onClick={handleCancel} type='default' disabled={loadingAddMembersTeam}>
								Cancel
							</Button>
							<Button
								onClick={handleSubmit}
								type='primary'
								loading={loadingAddMembersTeam}
								disabled={!hasChanges}
							>
								Save Changes
							</Button>
						</Flex>
					</>
				]}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Manage Members
						</Title>
						<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
							Manage people in this team by adding or removing people from the list.{' '}
						</Text>
					</Col>

					{/* Select new members */}
					<Col span={24}>
						<Flex vertical gap={paddingXS}>
							<Text strong>Add People</Text>
							<Select
								showSearch
								placeholder='Select People'
								style={{ width: '100%' }}
								options={availablePeople}
								value={null}
								onSelect={handleAddPerson}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
							/>
						</Flex>
					</Col>

					{/* Show existing members */}
					<Col span={24}>
						<Flex vertical gap={paddingSM}>
							<Text strong>Existing Members</Text>
							{(team?.members || []).filter((id) => !removedMembers.includes(id)).length === 0 &&
							newMembers.length === 0 ? (
								<Flex
									justify='center'
									align='center'
									style={{
										width: '100%',
										height: 85
									}}
								>
									<Empty
										image={Empty.PRESENTED_IMAGE_SIMPLE}
										description='No members in this team yet. Add people using the dropdown above.'
									/>
								</Flex>
							) : (
								<Flex
									wrap='wrap'
									gap={paddingSM}
									align='flex-start'
									style={{
										height: 85,
										overflowY: 'auto',
										alignContent: 'flex-start',
										alignItems: 'flex-start'
									}}
								>
									{' '}
									{/* Existing members (not marked for removal) */}
									{(team?.members || [])
										.filter((personID) => !removedMembers.includes(personID))
										.map((personID) => {
											const person = people.find((p) => p.personID === personID);
											return (
												<Tag
													key={`existing-${personID}`}
													color='purple'
													style={{
														fontWeight: fontWeightStrong,
														borderColor: 'currentColor',
														marginInlineEnd: 0
													}}
												>
													<Flex align='center' gap={paddingXXS}>
														<UserIcon color='currentColor' />
														{formatDisplayName(person)?.displayName}
														<CloseOutlined
															style={{ cursor: 'pointer', color: colorIcon }}
															onClick={() => handleRemovePerson(personID)}
														/>
													</Flex>
												</Tag>
											);
										})}
									{/* Newly added members */}
									{newMembers.map((personID) => {
										const person = people.find((p) => p.personID === personID);
										return (
											<Tag
												key={`new-${personID}`}
												color='purple'
												style={{
													fontWeight: fontWeightStrong,
													borderColor: 'currentColor',
													opacity: 0.7,
													marginInlineEnd: 0
												}}
											>
												<Flex align='center' gap={paddingXXS}>
													<UsergroupAddOutlined color='currentColor' />
													{formatDisplayName(person)?.displayName} (New)
													<CloseOutlined
														style={{ cursor: 'pointer', color: colorIcon }}
														onClick={() => handleRemovePerson(personID)}
													/>
												</Flex>
											</Tag>
										);
									})}
								</Flex>
							)}
						</Flex>
					</Col>
				</Row>
			</Modal>
		</>
	);
};

export default ManageMembersModal;
